/**
 * admin/makua/AdminDatedRetreats.js — Retreats → Retreat Schedule (V2.1, Page 3;
 * was "Dated retreats").
 *
 *   Schedule        operational blocks (the windows the team runs), each a
 *                   folding group holding its dated retreats; blocks are
 *                   made, edited and archived here. Opening a date shows the
 *                   same five tabs as before: Settings, Cohorts, Add-ons,
 *                   Guests, Waiting.
 *   Retreat types   the evergreen cards and the story each date inherits,
 *                   in the order staff set
 *   Waiting lists   everyone waiting for a place or for new dates
 *
 * Earlier Bookings (the older one-booking-per-person screen) is kept as a
 * read-only archive, linked at the foot of the schedule.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import AdminLayout from "../AdminLayout";
import ImagePicker from "../ImagePicker";
import { usePermissions, hasPermission } from "../permissions";
import { scheduledAPI, retreatTypesAPI, retreatBlocksAPI, bookingsAPI, waitlistAPI } from "../../services/api";
import ReservationDrawer from "./ReservationDrawer";
import { useLoad, Field, Modal, money, range, fmtDate, fmtStamp, Badge, StatusBadge, ScreeningBadge, today } from "./ops";
import { useStored, useSort, SortTh, useReorder, ReorderHandle, useUnsaved, changed } from "./kit";
import "./ops.css";

const TABS = [["schedule", "Schedule"], ["types", "Retreat types"], ["waitlist", "Waiting lists"]];

export default function AdminDatedRetreats() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "schedule";
  const setTab = (k) => { const p = new URLSearchParams(params); if (k === "schedule") p.delete("tab"); else p.set("tab", k); setParams(p, { replace: true }); };
  return (
    <AdminLayout pageTitle="Retreat Schedule" pageSubtitle="Operational blocks, the dated retreats in them, retreat types and waiting lists." permission="blocks.view" feature="reservations">
      <div className="ops">
        <div className="ops-tabs" role="tablist" aria-label="Retreat Schedule">
          {TABS.map(([k, label]) => <button key={k} role="tab" aria-selected={tab === k} className={`ops-tab ${tab === k ? "is-on" : ""}`} onClick={() => setTab(k)}>{label}</button>)}
        </div>
        {tab === "schedule" && <Schedule focusBlock={Number(params.get("block")) || null} />}
        {tab === "types" && <Types />}
        {tab === "waitlist" && <Waitlist />}
      </div>
    </AdminLayout>
  );
}

/* ══════════════════════ SCHEDULE ══════════════════════ */
const d10 = (v) => String(v || "").slice(0, 10);
const BLOCK_STATUS = { upcoming: ["Upcoming", "blue"], open: ["Open", "green"], in_progress: ["Running", "green"], completed: ["Completed", ""], cancelled: ["Cancelled", "red"] };

function Schedule({ focusBlock }) {
  const { admin } = usePermissions();
  const canEdit = hasPermission(admin, "blocks.edit");
  const [when, setWhen] = useStored("sched:when", "upcoming");
  const [q, setQ] = useStored("sched:q", "");
  const [status, setStatus] = useStored("sched:status", "");
  const [showArchived, setShowArchived] = useStored("sched:archived", false);
  const [folded, setFolded] = useStored("sched:folded", {});
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(null);
  const [editBlock, setEditBlock] = useState(null);
  const [debounced, setDebounced] = useState(q);
  useEffect(() => { const t = setTimeout(() => setDebounced(q), 250); return () => clearTimeout(t); }, [q]);

  const dates = useLoad(() => scheduledAPI.list({ scope: when, q: debounced.trim(), archived: showArchived ? "include" : "" }), [when, debounced, showArchived]);
  const blocks = useLoad(() => retreatBlocksAPI.list({ scope: "all", archived: showArchived ? "include" : "" }), [showArchived]);
  const reload = () => { dates.reload(); blocks.reload(); };

  const rows = (dates.data || []).filter((p) => !status || p.status_label === status);
  const now = today();
  const needle = debounced.trim().toLowerCase();
  const groups = useMemo(() => {
    const list = Array.isArray(blocks.data) ? blocks.data : [];
    const byBlock = new Map();
    rows.forEach((p) => byBlock.set(p.retreat_block_id, [...(byBlock.get(p.retreat_block_id) || []), p]));
    const inWindow = (b) => (when === "upcoming" ? d10(b.end_date) >= now : when === "past" ? d10(b.end_date) < now : true);
    const out = list
      .filter((b) => byBlock.has(b.id)
        || (!needle && !status && inWindow(b) && (!b.archived_at || showArchived))
        || (!!needle && `${b.name} ${b.location || ""}`.toLowerCase().includes(needle)))
      .map((b) => ({ block: b, dates: byBlock.get(b.id) || [] }));
    out.sort((x, y) => (when === "past" ? d10(y.block.start_date).localeCompare(d10(x.block.start_date)) : d10(x.block.start_date).localeCompare(d10(y.block.start_date))));
    return out;
  }, [blocks.data, rows, when, now, needle, status, showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!focusBlock || !groups.length) return;
    setFolded((f) => ({ ...f, [focusBlock]: false }));
    setTimeout(() => document.getElementById(`block-${focusBlock}`)?.scrollIntoView({ block: "start", behavior: "smooth" }), 50);
  }, [focusBlock, groups.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const blockAction = async (b, action) => {
    try {
      const r = action === "delete" ? await retreatBlocksAPI.remove(b.id) : await retreatBlocksAPI.update(b.id, { archived: action === "archive" });
      toast.success(r.message); reload();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <>
      <div className="ops-bar ops-toolbar">
        <Field label="Search" className="ops-search"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Retreat, block, type, or a date (2026-11-08)" /></Field>
        <div className="ops-field"><span>When</span>
          <div className="ops-seg" role="group" aria-label="When">
            {[["upcoming", "Upcoming"], ["past", "Past"], ["all", "All"]].map(([k, l]) => <button key={k} type="button" aria-pressed={when === k} onClick={() => setWhen(k)}>{l}</button>)}
          </div>
        </div>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Any</option><option>Open</option><option>Full</option><option>Draft</option><option>Closed</option></select>
        </Field>
        <label className="ops-check"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />Show archived blocks</label>
        <span className="ops-spacer" />
        {canEdit && <><button className="ops-btn" onClick={() => setEditBlock({})}>New block</button>
          <button className="ops-btn ops-btn--primary" onClick={() => setCreating({})}>New dated retreat</button></>}
      </div>
      {(dates.error || blocks.error) && <p className="ops-error">{dates.error || blocks.error}</p>}
      {(dates.loading || blocks.loading) && !groups.length && <p className="ops-muted">Loading…</p>}
      {!dates.loading && !blocks.loading && !groups.length && <p className="ops-note">Nothing here{needle ? ` for "${q}"` : ""}. {canEdit ? "Use New block, then New dated retreat." : ""}</p>}

      {groups.map(({ block: b, dates: ds }) => {
        const isOpen = !folded[b.id];
        const [sl, st] = BLOCK_STATUS[b.status] || [b.status, ""];
        const taken = ds.reduce((n, p) => n + p.seats_taken, 0), limit = ds.reduce((n, p) => n + p.seat_limit, 0);
        return (
          <section key={b.id} id={`block-${b.id}`} className="ops-card" style={b.archived_at ? { opacity: 0.7 } : null} aria-label={`Block ${b.name}`}>
            <div className="ops-card-head" style={{ flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", minWidth: 0 }}>
                <button type="button" className="ops-icon-btn" aria-expanded={isOpen} aria-controls={`block-body-${b.id}`} aria-label={`${isOpen ? "Fold" : "Unfold"} ${b.name}`}
                  onClick={() => setFolded({ ...folded, [b.id]: isOpen })}>{isOpen ? "▾" : "▸"}</button>
                <div>
                  <h3 style={{ margin: 0 }}>{b.name} <Badge tone={st}>{sl}</Badge> {b.archived_at && <Badge tone="red">Archived</Badge>}</h3>
                  <span className="ops-sub">{range(d10(b.start_date), d10(b.end_date))}{b.location ? ` · ${b.location}` : ""} · {ds.length} dated retreat{ds.length === 1 ? "" : "s"}{ds.length ? ` · ${taken} of ${limit} places taken · ${money(ds.reduce((n, p) => n + p.booked_value, 0))} booked` : ""}</span>
                </div>
              </div>
              {canEdit && (
                <div className="ops-actions">
                  <button className="ops-btn ops-btn--sm" onClick={() => setCreating({ block: b.id })}>Add dated retreat</button>
                  <button className="ops-btn ops-btn--sm" onClick={() => setEditBlock(b)}>Edit block</button>
                  {b.archived_at
                    ? <button className="ops-btn ops-btn--sm" onClick={() => blockAction(b, "restore")}>Restore</button>
                    : <button className="ops-btn ops-btn--sm" onClick={() => window.confirm(`Archive ${b.name}? It's kept, with its dated retreats and bookings, and can be restored.`) && blockAction(b, "archive")}>Archive</button>}
                </div>
              )}
            </div>
            <div id={`block-body-${b.id}`} hidden={!isOpen}>
              {ds.length ? <DatesTable rows={ds} onOpen={setOpen} /> : <p className="ops-note">No dated retreats in this block{status || needle ? " match" : " yet"}.</p>}
              {canEdit && !ds.length && !b.archived_at && (
                <div className="ops-actions" style={{ justifyContent: "flex-start", marginTop: "0.5rem" }}>
                  <button className="ops-btn ops-btn--sm ops-btn--danger" onClick={() => window.confirm(`Delete ${b.name}? An unused block is deleted; one with any history is archived instead.`) && blockAction(b, "delete")}>Delete block</button>
                </div>
              )}
            </div>
          </section>
        );
      })}

      <p className="ops-note">
        Older bookings made before reservations (one booking per person, per block) are kept in <Link to="/admin/bookings">Earlier Bookings</Link>, as a read-only archive. Nothing there was changed or removed.
      </p>
      {open && <DateDrawer id={open} onClose={() => setOpen(null)} onChanged={reload} />}
      {creating && <NewDate preset={creating} blocks={(Array.isArray(blocks.data) ? blocks.data : []).filter((b) => !b.archived_at)} onClose={() => setCreating(null)} onCreated={(id) => { setCreating(null); reload(); setOpen(id); }} />}
      {editBlock && <BlockModal b={editBlock} onClose={() => setEditBlock(null)} onSaved={() => { setEditBlock(null); reload(); }} />}
    </>
  );
}

function DatesTable({ rows, onOpen }) {
  const accessors = useMemo(() => ({
    name: (p) => p.name, start: (p) => p.start_date, status: (p) => p.status_label, places: (p) => p.seats_taken,
    waiting: (p) => Number(p.waitlist), price: (p) => Number(p.price), value: (p) => p.booked_value,
  }), []);
  const { rows: sorted, sort, setSort } = useSort(rows, accessors, "start");
  return (
    <div className="ops-table-wrap" style={{ border: 0 }}>
      <table className="ops-table">
        <thead><tr>
          <SortTh k="name" sort={sort} setSort={setSort}>Retreat</SortTh>
          <SortTh k="start" sort={sort} setSort={setSort}>Dates</SortTh>
          <SortTh k="status" sort={sort} setSort={setSort}>Status</SortTh>
          <SortTh k="places" sort={sort} setSort={setSort} className="num">Places</SortTh>
          <SortTh k="waiting" sort={sort} setSort={setSort} className="num">Waiting</SortTh>
          <SortTh k="price" sort={sort} setSort={setSort} className="num">Price</SortTh>
          <SortTh k="value" sort={sort} setSort={setSort} className="num">Booked value</SortTh>
        </tr></thead>
        <tbody>
          {sorted.map((p) => (
            <tr key={p.id}>
              <td><button className="ops-row-btn" onClick={() => onOpen(p.id)}>{p.name}</button><span className="ops-sub">{p.type_name || "No type"}</span></td>
              <td>{range(p.start_date, p.end_date)}<span className="ops-sub">{p.nights} nights{p.nights_before || p.nights_after ? ` · up to ${p.nights_before} before, ${p.nights_after} after` : ""}</span></td>
              <td><Badge tone={{ Open: "green", Full: "amber", Draft: "", Closed: "red" }[p.status_label]}>{p.status_label}</Badge></td>
              <td className="num">{p.seats_taken} / {p.seat_limit}{p.seat_limit < p.capacity && <span className="ops-sub">capacity {p.capacity}, cohorts {p.seat_limit}</span>}</td>
              <td className="num">{p.waitlist || "—"}</td>
              <td className="num">{money(p.price)}</td>
              <td className="num">{money(p.booked_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const blockForm = (b) => ({ name: b.name || "", start_date: d10(b.start_date), end_date: d10(b.end_date), location: b.location || "", notes: b.notes || "", status: b.status || "upcoming" });

function BlockModal({ b, onClose, onSaved }) {
  const base = useMemo(() => blockForm(b), [b]);
  const [f, setF] = useState(base);
  const [error, setError] = useState("");
  const dirty = changed(f, base);
  const guard = useUnsaved(dirty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setError("");
    try {
      const r = b.id ? await retreatBlocksAPI.update(b.id, f) : await retreatBlocksAPI.create(f);
      toast.success(r.message); onSaved();
    } catch (err) { setError(err.message); }
  };
  return (
    <Modal title={b.id ? `Edit ${b.name}` : "New operational block"} onClose={guard(onClose)}>
      <p className="ops-muted" style={{ margin: 0 }}>A block is the window of dates the team runs. It can hold several dated retreats, overlapping or not; guests book the dated retreats.</p>
      <div className="ops-grid ops-grid--2">
        <Field label="Name" className="ops-span-all"><input value={f.name} onChange={set("name")} placeholder="November 2026 program" /></Field>
        <Field label="Starts"><input type="date" value={f.start_date} onChange={set("start_date")} /></Field>
        <Field label="Ends"><input type="date" value={f.end_date} min={f.start_date} onChange={set("end_date")} /></Field>
        <Field label="Location"><input value={f.location} onChange={set("location")} /></Field>
        <Field label="Status">
          <select value={f.status} onChange={set("status")}><option value="upcoming">Upcoming</option><option value="open">Open</option><option value="in_progress">Running</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></select>
        </Field>
        <Field label="Notes for the team" className="ops-span-all"><textarea value={f.notes} onChange={set("notes")} rows={3} /></Field>
      </div>
      {b.id && <p className="ops-note">Changing the block's dates doesn't move its dated retreats or anyone's stay. Change those on each dated retreat.</p>}
      {error && <p className="ops-error">{error}</p>}
      <div className="ops-actions">
        {dirty && <span className="ops-dirty">Unsaved changes</span>}
        <button className="ops-btn" onClick={guard(onClose)}>Cancel</button>
        <button className="ops-btn ops-btn--primary" disabled={!f.name.trim() || !f.start_date || !f.end_date || (b.id && !dirty)} onClick={save}>{b.id ? "Save changes" : "Add block"}</button>
      </div>
    </Modal>
  );
}

function NewDate({ preset, blocks, onClose, onCreated }) {
  const { data: types } = useLoad(() => retreatTypesAPI.list(), []);
  const first = blocks.find((b) => b.id === preset.block);
  const [f, setF] = useState({ block: preset.block || "", retreat_type_id: "", name: "", start_date: first ? d10(first.start_date) : "", end_date: first ? d10(first.end_date) : "", capacity: 12, price: "", public_listed: false });
  const [error, setError] = useState("");
  const save = async () => {
    setError("");
    try {
      const res = await bookingsAPI.createPathway(f.block, { ...f, active: true, retreat_type_id: f.retreat_type_id || null });
      toast.success("Dated retreat added. Finish setting it up, then publish it.");
      onCreated(res.data.id);
    } catch (err) { setError(err.message); }
  };
  return (
    <Modal title="New dated retreat" onClose={onClose} wide>
      <div className="ops-grid ops-grid--2">
        <Field label="Operational block" hint="The window of dates the team is running.">
          <select value={f.block} onChange={(e) => {
            const b = blocks.find((x) => String(x.id) === e.target.value);
            setF({ ...f, block: e.target.value, start_date: f.start_date || (b ? d10(b.start_date) : ""), end_date: f.end_date || (b ? d10(b.end_date) : "") });
          }}>
            <option value="">Choose…</option>
            {blocks.map((b) => <option key={b.id} value={b.id}>{b.name} · {range(d10(b.start_date), d10(b.end_date))}</option>)}
          </select>
        </Field>
        <Field label="Retreat type" hint="Its card, story and itinerary are shared by every date of this type.">
          <select value={f.retreat_type_id} onChange={(e) => {
            const t = (types || []).find((x) => String(x.id) === e.target.value);
            setF({ ...f, retreat_type_id: e.target.value, name: f.name || t?.name || "" });
          }}>
            <option value="">Choose…</option>
            {(types || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="Name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Price per person (USD)"><input type="number" min="0" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></Field>
        <Field label="Arrive"><input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></Field>
        <Field label="Leave"><input type="date" value={f.end_date} min={f.start_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></Field>
        <Field label="Places" hint="The most this date may sell. Cohorts can lower it."><input type="number" min="0" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} /></Field>
      </div>
      <p className="ops-note">New dated retreats start unpublished, so you can add cohorts, add-ons and extra nights before anyone can book.</p>
      {error && <p className="ops-error">{error}</p>}
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Cancel</button>
        <button className="ops-btn ops-btn--primary" disabled={!f.block || !f.name || !f.start_date || !f.end_date || f.price === ""} onClick={save}>Add dated retreat</button></div>
    </Modal>
  );
}

const SETTINGS_FIELDS = (p) => ({
  name: p.name, retreat_type_id: p.retreat_type_id || "", tagline: p.tagline || "", description: p.description || "",
  itinerary: p.itinerary || "", image_path: p.image_path || "",
  start_date: p.start_date, end_date: p.end_date, capacity: p.capacity, price: Number(p.price),
  active: !!p.active, public_listed: !!p.public_listed, max_party: p.max_party,
  nights_before: p.nights_before, nights_after: p.nights_after, extra_night_price: Number(p.extra_night_price), extension_requests: !!p.extension_requests,
  second_guest_mode: p.second_guest_mode, second_guest_value: Number(p.second_guest_value),
  early_discount: Number(p.early_discount), early_discount_until: p.early_discount_until || "",
  included_addons: p.included_addons, decide_later: !!p.decide_later, waitlist_enabled: !!p.waitlist_enabled, cohort_policy: p.cohort_policy,
});

function DateDrawer({ id, onClose, onChanged }) {
  const { data, loading, error, reload } = useLoad(() => scheduledAPI.get(id), [id]);
  const [tab, setTab] = useState("settings");
  // Unsaved settings ask before the drawer closes or the tab changes.
  const guardRef = useRef(null);
  const guarded = (fn) => () => (guardRef.current && tab === "settings" ? guardRef.current(fn)() : fn());
  const [openRes, setOpenRes] = useState(null);
  const refresh = () => { reload(); onChanged?.(); };
  const p = data?.retreat;
  return (
    <>
      <div className="ops-drawer-scrim" onClick={guarded(onClose)} />
      <aside className="ops-drawer" aria-label="Retreat date">
        <div className="ops-drawer-head">
          <div><h2>{p ? p.name : "Retreat"}</h2>{p && <p className="ops-muted" style={{ margin: "0.2rem 0 0" }}>{range(p.start_date, p.end_date)} · {data.seats.taken} of {data.seats.limit} places taken</p>}</div>
          <button type="button" className="ops-close" aria-label="Close" onClick={guarded(onClose)}>×</button>
        </div>
        <div className="ops-drawer-body">
          {loading && !p && <p className="ops-muted">Loading…</p>}
          {error && <p className="ops-error">{error}</p>}
          {p && (
            <>
              <div className="ops-tabs">
                {[["settings", "Settings"], ["cohorts", `Cohorts (${data.cohorts.length})`], ["sessions", `Add-ons (${data.sessions.length})`], ["roster", `Guests (${data.roster.length})`], ["waiting", `Waiting (${data.waitlist.length})`]]
                  .map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} className={`ops-tab ${tab === k ? "is-on" : ""}`} onClick={k === tab ? undefined : guarded(() => setTab(k))}>{l}</button>)}
              </div>
              {tab === "settings" && <Settings p={p} types={data.types} onSaved={refresh} onGone={() => { onChanged?.(); onClose(); }} guardRef={guardRef} />}
              {tab === "cohorts" && <Cohorts p={p} data={data} onChanged={refresh} />}
              {tab === "sessions" && <Sessions p={p} sessions={data.sessions} onChanged={refresh} />}
              {tab === "roster" && <Roster roster={data.roster} onOpen={setOpenRes} />}
              {tab === "waiting" && <WaitRows rows={data.waitlist} onChanged={refresh} />}
            </>
          )}
        </div>
      </aside>
      {openRes && <ReservationDrawer id={openRes} onClose={() => setOpenRes(null)} onChanged={refresh} />}
    </>
  );
}

function Settings({ p, types, onSaved, onGone, guardRef }) {
  const [base, setBase] = useState(() => SETTINGS_FIELDS(p));
  const [f, setF] = useState(base);
  const [saving, setSaving] = useState(false);
  const dirty = changed(f, base);
  const guard = useUnsaved(dirty);
  if (guardRef) guardRef.current = guard;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const res = await bookingsAPI.updatePathway(p.id, { ...f, retreat_type_id: f.retreat_type_id || null, early_discount_until: f.early_discount_until || null });
      toast.success(res.message || "Saved."); setBase(f); onSaved();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${p.name}? A date with reservations, bookings or a waiting list is archived instead: unpublished and closed, with its history kept.`)) return;
    try { const r = await bookingsAPI.removePathway(p.id); toast.success(r.message); onGone(); } catch (err) { toast.error(err.message); }
  };
  return (
    <>
      <div className="ops-card">
        <h3>Publication</h3>
        <div className="ops-grid ops-grid--3">
          <label className="ops-check"><input type="checkbox" checked={f.public_listed} onChange={set("public_listed")} />Published on the website</label>
          <label className="ops-check"><input type="checkbox" checked={f.active} onChange={set("active")} />Taking bookings</label>
          <label className="ops-check"><input type="checkbox" checked={f.waitlist_enabled} onChange={set("waitlist_enabled")} />Waiting list when full</label>
        </div>
      </div>
      <div className="ops-card">
        <h3>The retreat</h3>
        <div className="ops-grid ops-grid--2">
          <Field label="Name"><input value={f.name} onChange={set("name")} /></Field>
          <Field label="Retreat type"><select value={f.retreat_type_id} onChange={set("retreat_type_id")}><option value="">None</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
          <Field label="Arrive"><input type="date" value={f.start_date} onChange={set("start_date")} /></Field>
          <Field label="Leave"><input type="date" value={f.end_date} onChange={set("end_date")} /></Field>
          <Field label="Line under the name"><input value={f.tagline} onChange={set("tagline")} /></Field>
          <div><ImagePicker label="Picture for this date" hint="Empty uses the type's card picture." value={f.image_path} onChange={(v) => setF({ ...f, image_path: v })} /></div>
          <Field label="Description (leave empty to use the type's)" className="ops-span-2"><textarea value={f.description} onChange={set("description")} /></Field>
          <Field label="Itinerary for this date, one line each (leave empty to use the type's)" className="ops-span-2"><textarea value={f.itinerary} onChange={set("itinerary")} /></Field>
        </div>
      </div>
      <div className="ops-card">
        <h3>Places and price</h3>
        <div className="ops-grid ops-grid--3">
          <Field label="Places" hint="Never sold beyond this, or beyond the cohorts."><input type="number" min="0" value={f.capacity} onChange={set("capacity")} /></Field>
          <Field label="Price per person (USD)"><input type="number" min="0" value={f.price} onChange={set("price")} /></Field>
          <Field label="Most guests in one booking"><input type="number" min="1" max="50" value={f.max_party} onChange={set("max_party")} /></Field>
          <Field label="Second guest sharing a private room">
            <select value={f.second_guest_mode} onChange={set("second_guest_mode")}><option value="none">Pays full price</option><option value="percent">Percentage off</option><option value="fixed">Amount off</option></select>
          </Field>
          {f.second_guest_mode !== "none" && <Field label={f.second_guest_mode === "percent" ? "Percent off" : "Amount off (USD)"}><input type="number" min="0" value={f.second_guest_value} onChange={set("second_guest_value")} /></Field>}
          <Field label="Early-booking saving per person (USD)"><input type="number" min="0" value={f.early_discount} onChange={set("early_discount")} /></Field>
          <Field label="Early booking ends"><input type="date" value={f.early_discount_until} onChange={set("early_discount_until")} /></Field>
        </div>
      </div>
      <div className="ops-card">
        <h3>Extra nights</h3>
        <div className="ops-grid ops-grid--3">
          <Field label="Nights open before"><input type="number" min="0" max="14" value={f.nights_before} onChange={set("nights_before")} /></Field>
          <Field label="Nights open after"><input type="number" min="0" max="14" value={f.nights_after} onChange={set("nights_after")} /></Field>
          <Field label="Per guest per night (USD)" hint="Plus the room's upgrade rate."><input type="number" min="0" value={f.extra_night_price} onChange={set("extra_night_price")} /></Field>
        </div>
        <label className="ops-check"><input type="checkbox" checked={f.extension_requests} onChange={set("extension_requests")} />Let guests ask for other nights (coordinated with Cerro Tusa; never holds a room)</label>
      </div>
      <div className="ops-card">
        <h3>Add-ons and cohorts</h3>
        <div className="ops-grid ops-grid--3">
          <Field label="Included activities each guest may choose" hint="0 = all of them"><input type="number" min="0" max="20" value={f.included_addons} onChange={set("included_addons")} /></Field>
          <Field label="Cohort growth">
            <select value={f.cohort_policy} onChange={set("cohort_policy")}>
              <option value="fixed">Fixed: first cohort only</option><option value="manual">Manual: staff approve the next cohort</option><option value="auto">Automatic: open proposed cohorts as needed</option>
            </select>
          </Field>
          <label className="ops-check"><input type="checkbox" checked={f.decide_later} onChange={set("decide_later")} />Guests may decide add-ons later</label>
        </div>
      </div>
      <div className="ops-actions">
        <button className="ops-btn ops-btn--danger" style={{ marginRight: "auto" }} onClick={remove}>Delete or archive this date</button>
        {dirty && <span className="ops-dirty">Unsaved changes</span>}
        <button className="ops-btn ops-btn--primary" disabled={saving || !dirty} onClick={save}>{saving ? "Saving…" : "Save changes"}</button>
      </div>
    </>
  );
}

function Cohorts({ p, data, onChanged }) {
  const [edit, setEdit] = useState(null);
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    try { await scheduledAPI.removeCohort(p.id, c.id); toast.success("Removed."); onChanged(); } catch (err) { toast.error(err.message); }
  };
  return (
    <div className="ops-card">
      <div className="ops-card-head"><h3>Cohorts</h3><button className="ops-btn ops-btn--sm" onClick={() => setEdit({})}>Add cohort</button></div>
      <p className="ops-muted">Internal groups; guests never see or choose them. Approved cohorts set the ceiling: with cohorts of 15 and a second one only proposed, guest 16 is waitlisted until it's approved{data.seats.policy === "auto" ? " (or, on automatic, opened by the system)" : ""}.</p>
      {data.cohorts.length ? (
        <table className="ops-table">
          <thead><tr><th>Cohort</th><th>Facilitators</th><th>Status</th><th className="num">Guests</th><th /></tr></thead>
          <tbody>{data.cohorts.map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td><td>{c.facilitators || "—"}</td>
              <td><Badge tone={c.status === "approved" ? "green" : "amber"}>{c.status}</Badge></td>
              <td className="num">{c.taken} / {c.capacity}</td>
              <td className="num"><button className="ops-btn ops-btn--sm" onClick={() => setEdit(c)}>Edit</button> <button className="ops-btn ops-btn--sm" onClick={() => remove(c)}>Remove</button></td>
            </tr>))}
          </tbody>
        </table>
      ) : <p className="ops-note">No cohorts: the date's own number of places is the only limit.</p>}
      {edit && <CohortModal p={p} c={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChanged(); }} />}
    </div>
  );
}

function CohortModal({ p, c, onClose, onSaved }) {
  const [f, setF] = useState({ name: c.name || "", capacity: c.capacity || 15, facilitators: c.facilitators || "", status: c.status || "approved" });
  const save = async () => {
    try { await scheduledAPI.saveCohort(p.id, f, c.id); toast.success("Saved."); onSaved(); } catch (err) { toast.error(err.message); }
  };
  return (
    <Modal title={c.id ? `Edit ${c.name}` : "New cohort"} onClose={onClose}>
      <div className="ops-grid ops-grid--2">
        <Field label="Name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Cohort A" /></Field>
        <Field label="Guests"><input type="number" min="1" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} /></Field>
        <Field label="Facilitators" className="ops-span-2"><input value={f.facilitators} onChange={(e) => setF({ ...f, facilitators: e.target.value })} /></Field>
        <Field label="Status" hint="Only approved cohorts can be sold (or proposed ones, on automatic)." className="ops-span-2">
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}><option value="approved">Approved: staffing and ceremony capacity confirmed</option><option value="proposed">Proposed: not yet authorised</option></select>
        </Field>
      </div>
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Back</button><button className="ops-btn ops-btn--primary" disabled={!f.name || !f.capacity} onClick={save}>Save</button></div>
    </Modal>
  );
}

function Sessions({ p, sessions, onChanged }) {
  const { data: lib } = useLoad(() => bookingsAPI.catalog.activities(), []);
  const [edit, setEdit] = useState(null);
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name} from this date?`)) return;
    try { const r = await scheduledAPI.removeSession(p.id, s.id); toast.success(r.message); onChanged(); } catch (err) { toast.error(err.message); }
  };
  return (
    <div className="ops-card">
      <div className="ops-card-head"><h3>Add-ons offered on this date</h3><button className="ops-btn ops-btn--sm" onClick={() => setEdit({})}>Add from library</button></div>
      {sessions.length ? (
        <table className="ops-table">
          <thead><tr><th>Add-on</th><th>When</th><th>Offered as</th><th className="num">Price</th><th className="num">Taken</th><th /></tr></thead>
          <tbody>{sessions.map((s) => (
            <tr key={s.id} style={s.active ? null : { opacity: 0.5 }}>
              <td>{s.name}<span className="ops-sub">{s.category}{s.requires_clearance ? " · needs clearance" : ""}</span></td>
              <td>{s.activity_date ? fmtDate(s.activity_date) : "—"}{s.session_time && <span className="ops-sub">{s.session_time}</span>}</td>
              <td><Badge tone={s.inclusion === "included" ? "green" : s.inclusion === "request" ? "purple" : "blue"}>{s.inclusion}</Badge></td>
              <td className="num">{s.inclusion === "included" ? "—" : money(s.price_each)}</td>
              <td className="num">{s.taken} / {s.capacity || "∞"}</td>
              <td className="num"><button className="ops-btn ops-btn--sm" onClick={() => setEdit(s)}>Edit</button> <button className="ops-btn ops-btn--sm" onClick={() => remove(s)}>Remove</button></td>
            </tr>))}
          </tbody>
        </table>
      ) : <p className="ops-note">Nothing offered yet. The add-on step is skipped for guests until you add something.</p>}
      {edit && <SessionModal p={p} s={edit} lib={lib || []} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChanged(); }} />}
    </div>
  );
}

function SessionModal({ p, s, lib, onClose, onSaved }) {
  const [f, setF] = useState({
    activity_id: s.activity_id || "", inclusion: s.inclusion || "paid", price: s.price ?? "", capacity: s.capacity ?? 10,
    activity_date: s.activity_date || "", session_time: s.session_time || "", active: s.active ?? true,
  });
  const a = lib.find((x) => String(x.id) === String(f.activity_id));
  const save = async () => {
    try { await scheduledAPI.saveSession(p.id, { ...f, price: f.price === "" ? null : f.price }, s.id); toast.success("Saved."); onSaved(); } catch (err) { toast.error(err.message); }
  };
  return (
    <Modal title={s.id ? `Edit ${s.name}` : "Offer an add-on"} onClose={onClose}>
      <div className="ops-grid ops-grid--2">
        {!s.id && (
          <Field label="From the library" className="ops-span-2">
            <select value={f.activity_id} onChange={(e) => {
              // Start from the add-on's usual way of being offered and its usual places.
              const pick = lib.find((x) => String(x.id) === e.target.value);
              setF({ ...f, activity_id: e.target.value, ...(pick ? { inclusion: pick.default_inclusion || "paid", capacity: pick.default_capacity ?? f.capacity } : {}) });
            }}>
              <option value="">Choose…</option>{lib.filter((x) => x.active).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Offered as">
          <select value={f.inclusion} onChange={(e) => setF({ ...f, inclusion: e.target.value })}>
            <option value="included">Included in the retreat</option><option value="paid">Paid add-on</option><option value="request">On request</option>
          </select>
        </Field>
        <Field label="Price on this date (USD)" hint={a ? `Empty = library price ${money(a.retreat_price ?? a.unit_price)}` : "Empty = library price"}>
          <input type="number" min="0" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} disabled={f.inclusion === "included"} />
        </Field>
        <Field label="Places" hint="0 = unlimited"><input type="number" min="0" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} /></Field>
        <Field label="Date"><input type="date" value={f.activity_date} min={p.start_date} onChange={(e) => setF({ ...f, activity_date: e.target.value })} /></Field>
        <Field label="Time"><input value={f.session_time} onChange={(e) => setF({ ...f, session_time: e.target.value })} placeholder="Morning, 6:30pm…" /></Field>
        {s.id && <label className="ops-check"><input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />Offered to guests</label>}
      </div>
      <div className="ops-actions"><button className="ops-btn" onClick={onClose}>Back</button><button className="ops-btn ops-btn--primary" disabled={!s.id && !f.activity_id} onClick={save}>Save</button></div>
    </Modal>
  );
}

function Roster({ roster, onOpen }) {
  if (!roster.length) return <p className="ops-note">No one is booked on this date yet.</p>;
  return (
    <div className="ops-table-wrap">
      <table className="ops-table">
        <thead><tr><th>Guest</th><th>Room</th><th>Food</th><th>Screening</th><th>Reservation</th><th className="num">Balance</th></tr></thead>
        <tbody>{roster.map((g) => (
          <tr key={g.id}>
            <td>{g.first_name} {g.last_name}<span className="ops-sub">{g.email}</span></td>
            <td>{g.room_name ? `${g.room_name}${g.bed_name ? ` · ${g.bed_name}` : ""}` : g.room_type_name}<span className="ops-sub">{g.meal_service || ""}</span></td>
            <td>{g.diets || "—"}</td>
            <td><ScreeningBadge status={g.screening_status} /></td>
            <td><button className="ops-row-btn" onClick={() => onOpen(g.reservation_id)}>{g.ref}</button> <StatusBadge status={g.status} /></td>
            <td className="num">{money(g.balance)}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function WaitRows({ rows, onChanged }) {
  const update = async (w, status) => {
    try { await waitlistAPI.update(w.id, { status }); toast.success("Saved."); onChanged(); } catch (err) { toast.error(err.message); }
  };
  if (!rows.length) return <p className="ops-note">Nobody is waiting.</p>;
  return (
    <div className="ops-table-wrap">
      <table className="ops-table">
        <thead><tr><th>Name</th><th>For</th><th>Asked</th><th>Status</th><th /></tr></thead>
        <tbody>{rows.map((w) => (
          <tr key={w.id}>
            <td>{w.name}<span className="ops-sub"><a href={`mailto:${w.email}`}>{w.email}</a></span></td>
            <td>{w.retreat_name ? `${w.retreat_name}${w.start_date ? ` · ${fmtDate(w.start_date)}` : ""}` : w.type_name ? `${w.type_name} · any new date` : "—"}<span className="ops-sub">{w.guests} {w.guests === 1 ? "guest" : "guests"}</span></td>
            <td>{fmtStamp(w.created_at)}</td>
            <td><Badge tone={w.status === "waiting" ? "amber" : w.status === "booked" ? "green" : ""}>{w.status}</Badge></td>
            <td className="num">
              <select className="ops-input" aria-label={`Status for ${w.name}`} value={w.status} onChange={(e) => update(w, e.target.value)}>
                <option value="waiting">Waiting</option><option value="contacted">Contacted</option><option value="booked">Booked</option><option value="removed">Removed</option>
              </select>
            </td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Waitlist() {
  const [status, setStatus] = useState("waiting");
  const { data, error, reload } = useLoad(() => waitlistAPI.list({ status }), [status]);
  return (
    <>
      <div className="ops-bar">
        <Field label="Show">
          <select value={status} onChange={(e) => setStatus(e.target.value)}><option value="waiting">Waiting</option><option value="contacted">Contacted</option><option value="booked">Booked</option><option value="">Everyone</option></select>
        </Field>
        <p className="ops-muted" style={{ margin: 0, alignSelf: "center" }}>People on a full date's waiting list, and people who asked to hear when a retreat has new dates.</p>
      </div>
      {error && <p className="ops-error">{error}</p>}
      <WaitRows rows={data || []} onChanged={reload} />
    </>
  );
}

/* ══════════════════════ TYPES ══════════════════════ */
function Types() {
  const { admin } = usePermissions();
  const canEdit = hasPermission(admin, "blocks.edit");
  const { data, error, reload } = useLoad(() => retreatTypesAPI.list(), []);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const all = useMemo(() => data || [], [data]);
  const needle = q.trim().toLowerCase();
  const filtered = all.filter((t) => !needle || `${t.name} ${t.tagline || ""} ${t.slug}`.toLowerCase().includes(needle));
  const accessors = useMemo(() => ({ name: (t) => t.name, upcoming: (t) => Number(t.upcoming), shown: (t) => (t.active ? 0 : 1) }), []);
  const { rows, sort, setSort } = useSort(filtered, accessors, null, "types:sort");
  const canDrag = canEdit && !sort.key && !needle;
  const order = useReorder(rows, async (ids) => { await retreatTypesAPI.reorder(ids); toast.success("Order saved. The home page shows the cards in this order."); });
  const shown = canDrag ? order.items : rows;
  return (
    <>
      <div className="ops-bar ops-toolbar">
        <Field label="Search" className="ops-search"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or web address" /></Field>
        <p className="ops-muted" style={{ margin: 0, alignSelf: "center", maxWidth: "34rem" }}>The cards on the home page, in this order. Each dated retreat belongs to one and shows its story, itinerary and inclusions.</p>
        <span className="ops-spacer" />
        {sort.key && <button className="ops-btn ops-btn--sm" onClick={() => setSort({ key: null, dir: "asc" })}>Back to website order</button>}
        {canEdit && <button className="ops-btn ops-btn--primary" onClick={() => setEdit({})}>New type</button>}
      </div>
      {error && <p className="ops-error">{error}</p>}
      <div className="ops-table-wrap">
        <table className="ops-table">
          <thead><tr>
            {canDrag && <th><span className="ops-visually-hidden">Order</span></th>}
            <SortTh k="name" sort={sort} setSort={setSort}>Type</SortTh><th>Web address</th>
            <SortTh k="upcoming" sort={sort} setSort={setSort} className="num">Upcoming dates</SortTh>
            <SortTh k="shown" sort={sort} setSort={setSort}>On the site</SortTh><th />
          </tr></thead>
          <tbody>{shown.map((t) => (
            <tr key={t.id} {...(canDrag ? order.rowProps(t.id) : {})}>
              {canDrag && <td><ReorderHandle r={order} id={t.id} label={t.name} /></td>}
              <td><button className="ops-row-btn" onClick={() => setEdit(t)}>{t.name}</button><span className="ops-sub">{t.tagline || ""}</span></td>
              <td>/retreats/{t.slug}</td>
              <td className="num">{t.upcoming}</td>
              <td>{t.active ? <Badge tone="green">Shown</Badge> : <Badge>Hidden</Badge>}</td>
              <td className="num"><button className="ops-btn ops-btn--sm" onClick={() => setEdit(t)}>{canEdit ? "Edit" : "View"}</button></td>
            </tr>))}
          </tbody>
        </table>
      </div>
      {edit && <TypeModal t={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </>
  );
}

function TypeModal({ t, onClose, onSaved }) {
  const keys = ["name", "slug", "tagline", "description", "tags", "card_image", "hero_image", "itinerary", "inclusions", "eligibility", "preparation", "travel", "sort_order"];
  const [base] = useState(() => ({ ...Object.fromEntries(keys.map((k) => [k, t[k] ?? (k === "sort_order" ? 0 : "")])), active: t.id ? !!t.active : true }));
  const [f, setF] = useState(base);
  const dirty = changed(f, base);
  const guard = useUnsaved(dirty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { await retreatTypesAPI.save(f, t.id); toast.success("Saved."); onSaved(); } catch (err) { toast.error(err.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${t.name}?`)) return;
    try { const r = await retreatTypesAPI.remove(t.id); toast.success(r.message); onSaved(); } catch (err) { toast.error(err.message); }
  };
  return (
    <Modal title={t.id ? `Edit ${t.name}` : "New retreat type"} onClose={guard(onClose)} wide>
      <div className="ops-grid ops-grid--2">
        <Field label="Name"><input value={f.name} onChange={set("name")} /></Field>
        <Field label="Web address" hint={`makuaretreats.com/retreats/${f.slug || "…"}`}><input value={f.slug} onChange={set("slug")} placeholder="made from the name" /></Field>
        <Field label="Line under the name" className="ops-span-2"><input value={f.tagline} onChange={set("tagline")} /></Field>
        <Field label="Description" className="ops-span-2"><textarea value={f.description} onChange={set("description")} rows={4} /></Field>
        <Field label="Tags" hint="Comma separated: Trauma healing, Emotional release"><input value={f.tags} onChange={set("tags")} /></Field>
        <div><ImagePicker label="Card picture (the illustration)" value={f.card_image} onChange={(v) => setF({ ...f, card_image: v })} /></div>
        <div><ImagePicker label="Page picture" value={f.hero_image} onChange={(v) => setF({ ...f, hero_image: v })} /></div>
        <Field label="Itinerary, one line each"><textarea value={f.itinerary} onChange={set("itinerary")} rows={5} /></Field>
        <Field label="What's included, one line each"><textarea value={f.inclusions} onChange={set("inclusions")} rows={5} /></Field>
        <Field label="Who it's for"><textarea value={f.eligibility} onChange={set("eligibility")} /></Field>
        <Field label="Preparation and requirements"><textarea value={f.preparation} onChange={set("preparation")} /></Field>
        <Field label="Travel and arrival" className="ops-span-2"><textarea value={f.travel} onChange={set("travel")} /></Field>
      </div>
      <label className="ops-check"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />Show on the website</label>
      <div className="ops-actions">
        {t.id && <button className="ops-btn ops-btn--danger" onClick={remove} style={{ marginRight: "auto" }}>Delete</button>}
        {dirty && <span className="ops-dirty">Unsaved changes</span>}
        <button className="ops-btn" onClick={guard(onClose)}>Cancel</button><button className="ops-btn ops-btn--primary" disabled={!f.name.trim() || (t.id && !dirty)} onClick={save}>{t.id ? "Save changes" : "Add type"}</button>
      </div>
    </Modal>
  );
}
